import React, {
  useState, useCallback, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Popup, Icon } from 'semantic-ui-react';

// Fade duration; keep in sync with styles.css.
const POPUP_FADE_MS = 250;

// Info icon + hover/focus popup with the rule's description.
// Renders nothing (no icon) when there's no description.
const RuleInfoPopup = ({ rule }) => {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef(null);

  // Clear the fade-out timer on unmount.
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleOpen = useCallback(() => {
    clearTimeout(closeTimer.current);
    setClosing(false);
    setOpen(true);
  }, []);

  // Delay unmount so the fade-out can play.
  const handleClose = useCallback(() => {
    setClosing(true);
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, POPUP_FADE_MS);
  }, []);

  const infoText = (rule.description || '').trim();
  if (!infoText) return null;

  const popupId = `rule-info-${rule.id}`;

  return (
    <Popup
      id={popupId}
      size='small'
      position='top left'
      on={['hover', 'focus']}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      className={`field-info-popup${closing ? ' field-info-popup--closing' : ''}`}
      content={infoText}
      data-testid={`rule-info-popup-${rule.id}`}
      trigger={(
        <Icon
          name='info circle'
          className='field-info-icon'
          tabIndex={0}
          aria-label={`What the ${rule.name} rule does`}
          aria-describedby={popupId}
          data-testid={`rule-info-icon-${rule.id}`}
        />
      )}
    />
  );
};

RuleInfoPopup.propTypes = {
  rule: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
  }).isRequired,
};

export default RuleInfoPopup;
